import Cookies from 'js-cookie';
// routes
import axios from '@raas/shared/src/utils/axios';
import { APP_REGISTRATION_CLIENT_ID } from '@raas/shared/src/shared-config-global';
import { IUser } from '@raas/shared/src/types/user';
import usersService from '@raas/shared/src/service/api/UsersService';
import localStorageAvailable from '@raas/shared/src/utils/localStorageAvailable';
import { getMsalInstance } from './msalInstance';
// utils

let refreshTimer: NodeJS.Timeout | null = null;

// ----------------------------------------------------------------------

export function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

export const tokenExpired = (exp: number, loginPath: string) => {
  // eslint-disable-next-line prefer-const
  let expiredTimer;
  const currentTime = Date.now();
  const timeLeft = exp * 1000 - currentTime;

  clearTimeout(expiredTimer);

  expiredTimer = setTimeout(() => {
    localStorage.removeItem('accessToken');
    window.location.href = loginPath;
  }, timeLeft);
};

const request = {
  scopes: [`${APP_REGISTRATION_CLIENT_ID}/.default`],
  prompt: 'none',
  forceRefresh: false,
};

export const tokenRefresh = (
  expiration: number,
  loginPath: string,
  requiredPermission: string,
  setIdbForServiceWorker: (token: string) => Promise<void>,
  logout: () => Promise<void>
) => {
  const currentTime = Date.now();
  const timeLeft = expiration * 1000 - currentTime - 300_000;

  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  refreshTimer = setTimeout(async () => {
    try {
      const account = getMsalInstance().getAllAccounts()[0];
      const dynamicRequest = {
        ...request,
        account,
      };
      const response = await getMsalInstance().acquireTokenSilent(dynamicRequest);
      const { exp } = jwtDecode(response.accessToken);
      tokenRefresh(exp, loginPath, requiredPermission, setIdbForServiceWorker, logout);

      const { accessToken } = await usersService.createOrLogin(response.accessToken);
      setSession(accessToken, loginPath, requiredPermission, false);
      setIdbForServiceWorker(accessToken);
    } catch (error) {
      console.error('Silent token renewal failed: ', error);
      logout();
    }
  }, timeLeft);
};

// ----------------------------------------------------------------------

export const setSession = (
  accessToken: string | null,
  loginPath: string,
  requiredPermission: string,
  shouldExpire: boolean
) => {
  if (accessToken) {
    const { exp, hasExternalAccess, hasInternalAccess } = jwtDecode(accessToken);
    if (!(
      (requiredPermission === 'EXTERNAL_ACCESS' && hasExternalAccess) ||
      (requiredPermission === 'INTERNAL_ACCESS' && hasInternalAccess)
    )) {
      throw new Error('Missing permissions');
    }

    localStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    if (shouldExpire) {
      tokenExpired(exp, loginPath);
    }
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

export const mapUser = (user: IUser) => ({
  id: user.id,
  displayName: `${user.firstName} ${user.lastName}`,
  email: user.login,
  userType: user.userType,
});

export const getCurrentUser = async () => {
  const accessToken = localStorageAvailable() ? localStorage.getItem('accessToken') : '';
  if (accessToken && isValidToken(accessToken)) {
    const profile = await usersService.getProfile();
    return mapUser(profile);
  }
  return null;
};

// keycloak

export const clearCookie = () => {
  Cookies.remove('KEYCLOAK_IDENTITY');
  Cookies.remove('KEYCLOAK_SESSION');
};

export const getKeycloakToken = (): string | undefined => Cookies.get('KEYCLOAK_IDENTITY');
